import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Skeleton } from "@/components/Skeleton";
import { toast } from "sonner";
import { Lock, MessageCircle, Clock, Search, ShieldCheck, CheckCircle2, Inbox as InboxIcon } from "lucide-react";

export default function Inbox() {
  const navigate = useNavigate();
  const [connections, setConnections] = useState(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/connect/list");
        if (alive) setConnections(data);
      } catch {
        if (alive) toast.error("Could not load your inbox");
      }
    };
    load();
    const poll = setInterval(load, 8000);   // refresh list every 8s
    return () => { alive = false; clearInterval(poll); };
  }, []);

  const filtered = (connections || []).filter((c) => {
    const q = filter.trim().toLowerCase();
    if (!q) return true;
    return (
      c.counterpart?.display_name?.toLowerCase().includes(q) ||
      c.last_message_preview?.toLowerCase().includes(q)
    );
  });

  return (
    <Layout title="Secure Inbox">
      <div className="mb-5">
        <span className="tb-chip-lavender inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide">
          <Lock className="h-3 w-3" /> Secure Inbox
        </span>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight font-medium leading-tight">
          Private conversations
        </h1>
        <p className="mt-1.5 text-sm text-muted-foreground max-w-2xl">
          End-to-end private chats between you and the educators you've connected with.
          Only the two parties can read these messages.
        </p>
      </div>

      {/* Search */}
      <div className="relative mb-4 max-w-md" data-testid="inbox-search-wrap">
        <Search className="h-4 w-4 absolute top-1/2 -translate-y-1/2 left-3 text-muted-foreground" />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search by name or message…"
          className="w-full h-10 pl-9 pr-3 rounded-full border border-border bg-card text-sm focus:outline-none focus:border-primary"
          data-testid="inbox-search"
        />
      </div>

      {/* List */}
      {connections === null ? (
        <div className="space-y-2">
          <Skeleton className="h-20" />
          <Skeleton className="h-20" />
          <Skeleton className="h-20" />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyInbox />
      ) : (
        <ul className="space-y-2" data-testid="inbox-list">
          {filtered.map((c) => (
            <li key={c.connect_id}>
              <button
                onClick={() => navigate(`/inbox/${c.connect_id}`)}
                className={`w-full text-left rounded-2xl border p-4 flex items-start gap-3 transition group ${
                  c.is_expired
                    ? "border-border bg-muted/40 hover:bg-muted/60"
                    : "border-border bg-card hover:border-primary/40 hover:shadow-md"
                }`}
                data-testid={`inbox-row-${c.connect_id}`}
              >
                {/* Avatar */}
                <div className="relative shrink-0">
                  {c.counterpart?.photo_url ? (
                    <img src={c.counterpart.photo_url} alt="" className="h-12 w-12 rounded-full object-cover" />
                  ) : (
                    <span className="h-12 w-12 rounded-full bg-gradient-to-br from-violet-400 to-amber-300 grid place-items-center text-white font-medium">
                      {(c.counterpart?.display_name || "?").charAt(0).toUpperCase()}
                    </span>
                  )}
                  {/* Unread badge (rendered on either side depending on role) */}
                  {unreadCount(c) > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 h-5 min-w-[20px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold grid place-items-center">
                      {unreadCount(c)}
                    </span>
                  )}
                </div>

                {/* Body */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-medium text-sm leading-snug truncate">
                      {c.counterpart?.display_name || "Educator"}
                    </p>
                    <p className="text-[10px] text-muted-foreground shrink-0">
                      {formatTimeAgo(c.last_message_at)}
                    </p>
                  </div>
                  {c.counterpart?.headline && (
                    <p className="text-[11px] text-muted-foreground truncate">{c.counterpart.headline}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1 truncate">
                    {c.last_message_preview || "(no messages yet)"}
                  </p>
                  <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                    {c.is_expired ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-muted text-muted-foreground text-[10px] px-2 py-0.5 font-medium">
                        <Clock className="h-2.5 w-2.5" /> Expired · read-only
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-200 text-[10px] px-2 py-0.5 font-medium">
                        <CheckCircle2 className="h-2.5 w-2.5" /> {c.days_left} day{c.days_left === 1 ? "" : "s"} left
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                      <ShieldCheck className="h-2.5 w-2.5" /> Private
                    </span>
                  </div>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Layout>
  );
}

function unreadCount(c) {
  if (c.role === "requester") return c.unread_for_requester || 0;
  return c.unread_for_recipient || 0;
}

function formatTimeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

function EmptyInbox() {
  return (
    <div className="tb-card p-10 text-center max-w-md mx-auto" data-testid="inbox-empty">
      <span className="inline-grid place-items-center h-14 w-14 rounded-2xl bg-gradient-to-br from-violet-400/40 to-amber-300/40 mx-auto">
        <InboxIcon className="h-7 w-7 text-foreground/70" />
      </span>
      <p className="mt-4 font-display text-lg tracking-tight font-medium">No conversations yet</p>
      <p className="text-xs text-muted-foreground mt-1.5">
        Find an educator on TeeBee, hit <strong>Secure Connect</strong> on their profile, and
        unlock 7 days of private chat for ₹199.
      </p>
    </div>
  );
}
